import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useAnchoredMenuPosition } from './useAnchoredMenuPosition';
import './overflow-menu.css';

const NARROW_QUERY = '(max-width: 767px)';

function usePrefersSheet(sheetOnNarrow) {
  const [sheet, setSheet] = useState(() => {
    if (!sheetOnNarrow || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(NARROW_QUERY).matches;
  });

  useEffect(() => {
    if (!sheetOnNarrow || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      setSheet(false);
      return undefined;
    }
    const media = window.matchMedia(NARROW_QUERY);
    const sync = () => setSheet(media.matches);
    sync();
    media.addEventListener?.('change', sync);
    media.addListener?.(sync);
    return () => {
      media.removeEventListener?.('change', sync);
      media.removeListener?.(sync);
    };
  }, [sheetOnNarrow]);

  return sheet;
}

/**
 * 共用「更多」選單：button + role="menu"／menuitem。
 * Escape 與點擊外部關閉；關閉後焦點回到觸發按鈕。
 * 可傳 open／onOpenChange 由父層控制（例如與其他選單互斥）。
 * anchored：位於會裁切的 overflow 容器內時，以 fixed 錨定觸發按鈕，避免選單被裁切而點不到。
 * sheetOnNarrow：窄螢幕改為底部 sheet（scrim 關閉、不造成水平捲動）。
 */
export default function OverflowMenu({
  items = [],
  label,
  triggerContent,
  disabled = false,
  anchored = false,
  sheetOnNarrow = false,
  open: controlledOpen,
  onOpenChange,
  wrapClassName = '',
  triggerClassName = '',
  menuClassName = '',
  triggerTestId,
  menuTestId,
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const controlled = typeof controlledOpen === 'boolean';
  const open = controlled ? controlledOpen : uncontrolledOpen;
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  const prefersSheet = usePrefersSheet(sheetOnNarrow);
  const useSheet = prefersSheet && open;
  const anchoredStyle = useAnchoredMenuPosition(anchored && open && !prefersSheet, triggerRef, menuRef);

  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  const setOpen = useCallback((next) => {
    if (!controlled) setUncontrolledOpen(next);
    onOpenChangeRef.current?.(next);
  }, [controlled]);

  const close = useCallback((restoreFocus) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, [setOpen]);

  useEffect(() => {
    if (open) menuRef.current?.querySelector('[role="menuitem"]:not(:disabled)')?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event) {
      if (useSheet) {
        if (event.target?.dataset?.overflowScrim === 'true') close(false);
        return;
      }
      if (wrapRef.current && !wrapRef.current.contains(event.target)) close(false);
    }
    function onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
        return;
      }
      if (!menuRef.current || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      const options = [...menuRef.current.querySelectorAll('[role="menuitem"]:not(:disabled)')];
      if (!options.length) return;
      const index = options.indexOf(document.activeElement);
      event.preventDefault();
      if (event.key === 'Home') {
        options[0].focus();
        return;
      }
      if (event.key === 'End') {
        options[options.length - 1].focus();
        return;
      }
      if (event.key === 'ArrowDown') {
        options[(index + 1 + options.length) % options.length].focus();
        return;
      }
      if (event.key === 'ArrowUp') {
        options[(index - 1 + options.length) % options.length].focus();
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close, useSheet]);

  useEffect(() => {
    if (!useSheet) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [useSheet]);

  function select(item) {
    if (item.disabled || disabled) return;
    close(true);
    item.onSelect?.();
  }

  const menu = open ? (
    <div
      id={menuId}
      ref={menuRef}
      className={`${menuClassName}${useSheet ? ' overflow-menu__sheet-panel' : ''}`.trim()}
      role="menu"
      data-testid={menuTestId}
      style={anchored && !prefersSheet ? anchoredStyle : undefined}
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          data-testid={item.testId}
          disabled={Boolean(item.disabled) || disabled}
          onClick={() => select(item)}
        >
          {item.label}
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div className={wrapClassName} ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        data-testid={triggerTestId}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={disabled}
        onClick={() => setOpen(!open)}
      >
        {triggerContent}
      </button>
      {useSheet ? (
        <div className="overflow-menu__sheet-root" role="presentation">
          <button
            type="button"
            className="overflow-menu__sheet-scrim"
            data-overflow-scrim="true"
            aria-label="關閉選單"
            tabIndex={-1}
            onClick={() => close(true)}
          />
          {menu}
        </div>
      ) : menu}
    </div>
  );
}
